import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, Heart, MessageCircle, UserPlus, Calendar, ShoppingBag, Info } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 通知类型图标
const NOTIFICATION_ICONS = {
  follow: UserPlus,
  like_artwork: Heart,
  like_post: Heart,
  comment: MessageCircle,
  message: MessageCircle,
  system: Info,
  exhibition: Calendar,
  order: ShoppingBag,
};

const NotificationBell = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (user && token) {
      fetchUnreadCount();
      // 每30秒刷新一次未读数
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch unread count', err);
    }
  };

  const fetchRecentNotifications = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/notifications?limit=5`, { headers });
      setRecentNotifications(res.data);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!showDropdown) {
      fetchRecentNotifications();
    }
    setShowDropdown(!showDropdown);
  };

  const handleNotificationClick = async (notification) => {
    // 标记为已读
    if (!notification.is_read) {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        await axios.post(`${API}/notifications/read/${notification.id}`, {}, { headers });
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (err) {
        console.error('Failed to mark as read', err);
      }
    }
    
    setShowDropdown(false);
    
    // 跳转
    if (notification.link) {
      navigate(notification.link);
    } else if (notification.from_user_id) {
      navigate(`/profile/${notification.from_user_id}`);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    if (diffHours < 24) return `${diffHours}小时前`;
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* 铃铛按钮 */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-amber-700 hover:text-amber-900 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* 下拉框 */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-amber-100 overflow-hidden z-50">
          {/* 头部 */}
          <div className="px-4 py-3 border-b bg-amber-50 flex items-center justify-between">
            <span className="font-medium text-amber-900">消息通知</span>
            {unreadCount > 0 && (
              <span className="text-xs text-amber-600">{unreadCount} 条未读</span>
            )}
          </div>

          {/* 通知列表 */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-amber-600">
                <div className="w-6 h-6 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mx-auto"></div>
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="py-8 text-center text-amber-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-amber-300" />
                <p>暂无通知</p>
              </div>
            ) : (
              recentNotifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Info;
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 hover:bg-amber-50 cursor-pointer border-b last:border-b-0 ${!notification.is_read ? 'bg-amber-50/50' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {notification.from_user?.avatar ? (
                        <img
                          src={getImageUrl(notification.from_user.avatar)}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-amber-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-amber-900 line-clamp-2">
                          {notification.content}
                        </p>
                        <p className="text-xs text-amber-500 mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 rounded-full bg-amber-500 mt-2" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* 底部 */}
          <div className="px-4 py-3 border-t bg-amber-50">
            <button
              onClick={() => {
                setShowDropdown(false);
                navigate('/notifications');
              }}
              className="w-full text-center text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              查看全部通知
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
